import React, { useEffect, useState } from "react";
import Sidebar from "components/common/Sidebar";
import Header from "components/common/Header";
import allImgPaths from "assets/images";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    checkProjectStatus,
    getPatentNameById,
    getPatentVijilizedDetails,
    getUrlDetailsById,
} from "services/project";
import { ClaimAnalysisShimmerLoader } from "pages/loaders";

import useAppState from "context/useAppState";
import ReactApexChart from "react-apexcharts";
import { HEATMAP_COLOR_SCALE } from "utils/constant";

interface Claim {
    elements: {
        description: string;
        figure: string;
        hex_code: string;
        legend: { [key: string]: string }[];
        figurePreSignedUrl: string;
    }[];
    similarityScore: number | null;
    confidenceScore: number | null;
}

interface Patent {
    patentId: string;
    patentNumber: string;
    claims: { [key: string]: Claim };
    structure: string;
}


const ClaimAnalysisHeatmap: React.FC = () => {
    const { id: projectId } = useParams()
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const navigate = useNavigate();
    const {
        setHeaderDataLocal,
        setClickedHeatmapData,
        setVijilizeParamsData,
        setWalkthroughState,
        registerWalkthroughActions,
    } = useAppState("vijilize");

    const [APIData, setAPIData] = useState<any | null>(null);
    const [series, setSeries] = useState<any>([]);
    const [seriesIdMap, setSeriesIdMap] = useState<{ [key: string]: string }>(
        {}
    );
    const [matchedAbove, setMatchedAbove] = useState<number>(0);
    const location = useLocation();

    const [selectedSource, setSelectedSource] = useState<any>(null);
    const [showClaimLevelHeatmap, setShowClaimLevelHeatmap] =
        useState<boolean>(false);
    const [claimLevelSeries, setClaimLevelSeries] = useState<any>([]);
    const [isDetailedView] = useState<boolean>(false);

    useEffect(() => {
        fetchProjectStatus()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const fetchProjectStatus = async () => {
        try {
            const { data } = await checkProjectStatus(projectId)
            console.log({ data })
        } catch (error: any) {
            if (error?.statusCode == 404) {
                navigate('/project')
            }
            console.log({ getProjectStatusError: error })
        }
    }

    const sampleHeatmapData = [
        {
            name: "US9895202",
            data: [
                {
                    x: "US9895202.pdf",
                    y: 20,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 65,
                    claim: "claim1",
                },
            ],
        },
        {
            name: "Niraj",
            data: [
                {
                    x: "US9895202.pdf",
                    y: 45,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 85,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 95,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 10,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 100,
                    claim: "claim1",
                },
            ],
        },
        {
            name: "US9895204",
            data: [
                {
                    x: "US9895202.pdf",
                    y: 30,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 70,
                    claim: "claim1",
                },
            ],
        },
        {
            name: "pk",
            data: [
                {
                    x: "US9895202.pdf",
                    y: 0,
                    claim: "claim1",
                },
                {
                    x: "1736523020787",
                    y: 80,
                    claim: "claim1",
                },
            ],
        },
    ];

    const colorRanges = HEATMAP_COLOR_SCALE.map((item: any) => {
        const itemObject: any = {
            from: item.value,
            to: item.value,
            color: item.color,
            name: `${item.value}`,
        };

        if (item.value <= 40) {
            itemObject.foreColor = "#455468";
        }

        return itemObject;
    });

    // Show claim-level heatmap for the selected source
    const showClaimHeatmapForSource = async (
        sourceName: string,
        sourceId: string
    ) => {
        setSelectedSource({
            name: sourceName,
            id: sourceId,
        });
        const claimLevelData =
            APIData?.heatmap_claim_level_data?.[sourceId]?.heatmapData || [];
        const updatedClaimLevelData: any = await Promise.all(
            claimLevelData.map(async (item: any) => {
                if (item.data && Array.isArray(item.data)) {
                    await Promise.all(
                        item.data.map(async (dataItem: any) => {
                            if (dataItem.x && isValidObjectId(dataItem.x)) {
                                try {
                                    const urlResponse = await getUrlDetailsById(
                                        dataItem.x
                                    );
                                    if (urlResponse && urlResponse.data) {
                                        dataItem.originalX = dataItem.x;
                                        dataItem.x =
                                            urlResponse.data.url_label ||
                                            urlResponse.data.url;
                                    }
                                } catch (error) {
                                    console.error(
                                        "Error fetching URL details:",
                                        error
                                    );
                                }
                            }
                            return dataItem;
                        })
                    );
                }
                return item;
            })
        );

        setClaimLevelSeries(updatedClaimLevelData);
        setShowClaimLevelHeatmap(true);
    };

    // Return to main heatmap view
    const returnToMainHeatmap = () => {
        setSelectedSource(null);
        setShowClaimLevelHeatmap(false);
        setClaimLevelSeries([]);
    };

    // Publish this page so the Header surfaces the "Navigating the Heatmap"
    // guided walkthrough. Reset on unmount.
    useEffect(() => {
        setWalkthroughState({
            page: "heatmap",
            hasSourcePatent: false,
            hasTarget: false,
            addPatentModalOpen: false,
        });
        return () => {
            setWalkthroughState({
                page: "",
                hasSourcePatent: false,
                hasTarget: false,
                addPatentModalOpen: false,
            });
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Register the heatmap walkthrough action: drill into the first patent so
    // the claim-level boxes and the "Back to Patent Level" button render before
    // their steps. Re-registered when the data changes so the closure is fresh.
    useEffect(() => {
        registerWalkthroughActions({
            drillIntoFirstPatent: () => {
                const first = series?.[0];
                if (
                    !first ||
                    showClaimLevelHeatmap ||
                    !APIData?.heatmap_claim_level_data
                )
                    return;
                const sourceId = seriesIdMap[first.name] || first.id || first.name;
                showClaimHeatmapForSource(first.name, sourceId);
            },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [series, seriesIdMap, APIData, showClaimLevelHeatmap]);

    const options: any = {
        chart: {
            //   height: 350,
            type: "heatmap",
            events: {
                click: function (event: any, chartContext: any, opts: any) {
                    const { seriesIndex, dataPointIndex } = opts;
                    const series = opts.config.series;

                    if (dataPointIndex >= 0 && seriesIndex >= 0) {
                        if (isDetailedView && !showClaimLevelHeatmap) {
                            showClaimHeatmapForSource(
                                series[seriesIndex]?.name,
                                series[seriesIndex]?.id
                            );
                        } else {
                            // Clear sessionStorage cache to force fresh data load
                            sessionStorage.removeItem('vijilizeClaimData');
                            
                            const newRoute =
                                location?.pathname.split("/heatmap")[0];
                            navigate(newRoute, {
                                state: {
                                    forceRefresh: true
                                }
                            });
                            setClickedHeatmapData({
                                source: series[seriesIndex]?.name,
                                sourceId:
                                    selectedSource?.id ||
                                    series[seriesIndex]?.id,
                                claimKey:
                                    series[seriesIndex]?.data[dataPointIndex]
                                        ?.claim,
                                targetId:
                                    series[seriesIndex]?.data[dataPointIndex]
                                        ?.originalX ||
                                    series[seriesIndex]?.data[dataPointIndex]
                                        ?.x,
                            });
                        }
                    } else {
                        if (
                            APIData?.heatmap_claim_level_data &&
                            !showClaimLevelHeatmap &&
                            event &&
                            event?.isTrusted &&
                            event?.srcElement?.nodeName === "tspan" &&
                            event?.srcElement?.nextSibling?.nodeName === "title"
                        ) {
                            const clickedName =
                                event?.srcElement?.nextSibling?.innerHTML;
                            const clickedSeries = opts?.config?.series.find(
                                (seriesItem: any) =>
                                    seriesItem.name === clickedName
                            );

                            if (clickedSeries) {
                                const sourceId =
                                    seriesIdMap[clickedName] ||
                                    clickedSeries.id ||
                                    clickedName;
                                showClaimHeatmapForSource(
                                    clickedName,
                                    sourceId
                                );
                            }
                        }
                    }
                },
            },
        },

        plotOptions: {
            heatmap: {
                // enableShades:true,
                shadeIntensity: 0,
                radius: 0,
                useFillColorAsStroke: true,
                colorScale: {
                    ranges: colorRanges,
                },
            },
        },
        dataLabels: {
            enabled: true,
            formatter: function (val: any) {
                return `${val}%`;
            },
            style: {
                fontSize: "2px",
                fontWeight: "bold",
            },
        },
        legend: {
            show: false,
        },
        xaxis: {
            labels: {
                formatter: (val: string) => {
                    if (!val) return "";
                    const fullVal = typeof val === "string" ? val : String(val);

                    // Wrap text instead of trimming
                    const maxCharsPerLine = 20; // Adjust this value based on your needs
                    const words = fullVal.split(" ");
                    const lines = [];
                    let currentLine = "";

                    for (const word of words) {
                        if ((currentLine + word).length <= maxCharsPerLine) {
                            currentLine += (currentLine ? " " : "") + word;
                        } else {
                            if (currentLine) {
                                lines.push(currentLine);
                                currentLine = word;
                            } else {
                                // If a single word is longer than maxCharsPerLine, split it
                                lines.push(word.slice(0, maxCharsPerLine));
                                currentLine = word.slice(maxCharsPerLine);
                            }
                        }
                    }
                    if (currentLine) {
                        lines.push(currentLine);
                    }

                    // Limit to maximum 3 lines to prevent excessive height
                    const maxLines = 3;
                    if (lines.length > maxLines) {
                        lines.splice(
                            maxLines - 1,
                            lines.length - maxLines + 1,
                            lines
                                .slice(maxLines - 1)
                                .join(" ")
                                .slice(0, maxCharsPerLine) + "..."
                        );
                    }

                    return lines;
                },
                rotate: 0,
                hideOverlappingLabels: false,
                show: true,
                trim: true, // Disable trimming
                tickAmount: 5,
                maxHeight: 120, // Increased to accommodate multiple lines
                style: {
                    fontSize: "10px", // Slightly smaller font for better fit
                    textAnchor: "middle", // Center align the text
                },
            },
        },
        yaxis: {
            labels: {
                style: {
                    cssClass: APIData?.heatmap_claim_level_data
                        ? "cursor-pointer"
                        : "",
                },
            },
        },

        // tooltip: {
        //   custom: function ({ series, seriesIndex, dataPointIndex, w }:any) {
        //     const point = w.globals.initialSeries[seriesIndex].data[dataPointIndex];
        //     console.log("🚀 ~ w.globals.initialSeries[seriesIndex]:", w.globals.initialSeries[seriesIndex])
        //     const sourceName= w.globals.initialSeries[seriesIndex].name;
        //     const fullLabel = point?.x || "Unknown";
        //     const value = point?.y || "N/A";

        //     return `<div style="padding: 8px;">
        //       <strong>${sourceName} :</strong> ${value}<br/>
        //       <strong>X:</strong> ${fullLabel}<br/>
        //     </div>`;
        //   },
        // },

        // title: {
        //   text: "HeatMap Chart with Color Range",
        // },
        // colors: ['#F2F5FF', '#AEBFFF', '#1B4DFF', '#113997']
    };

    const isValidObjectId = (id: string): boolean => {
        const objectIdPattern = /^[0-9a-fA-F]{24}$/;
        return objectIdPattern.test(id);
    };

    const setPatentId = async (heatmapData: any) => {
        // Dedup lookups: the same target URL (and source patent) repeats across
        // many rows/cells of the heatmap, so cache each id and fetch it once.
        // This was an N+1 (one getUrlDetailsById per cell) that made the heatmap
        // take 35-40s to load on projects with many URLs. Caching the in-flight
        // promise also collapses concurrent duplicate requests.
        const patentNameCache = new Map<string, Promise<any>>();
        const urlCache = new Map<string, Promise<any>>();
        const lookupPatentName = (name: string) => {
            if (!patentNameCache.has(name)) {
                patentNameCache.set(name, getPatentNameById(name).catch(() => null));
            }
            return patentNameCache.get(name)!;
        };
        const lookupUrl = (idVal: string) => {
            if (!urlCache.has(idVal)) {
                urlCache.set(idVal, getUrlDetailsById(idVal).catch(() => null));
            }
            return urlCache.get(idVal)!;
        };
        return Promise.all(
            heatmapData.map(async (item: any) => {
                if (item.name.includes("_")) {
                    item.name = item.name.split("_")[1];
                    item.id = item.name.split("_")[1];
                } else {
                    const response = await lookupPatentName(item.name);

                    if (response && response.data) {
                        item.name = response.data.patent_id;
                        item.id = response.data._id;
                    }
                }

                if (item.data && Array.isArray(item.data)) {
                    await Promise.all(
                        item.data.map(async (dataItem: any) => {
                            if (dataItem.x && isValidObjectId(dataItem.x)) {
                                const urlResponse = await lookupUrl(dataItem.x);
                                if (urlResponse && urlResponse.data) {
                                    dataItem.originalX = dataItem.x;
                                    dataItem.x =
                                        urlResponse.data.url_label ||
                                        urlResponse.data.url;
                                }
                            }
                            return dataItem;
                        })
                    );
                }

                return item;
            })
        );
    };

    useEffect(() => {
        setIsLoading(true);
        
        // Check if we need to refresh due to reanalysis
        const shouldRefresh = location.state?.reanalysisComplete || location.state?.forceRefresh;
        if (shouldRefresh) {
            console.log("Reanalysis complete - refreshing heatmap data");
        }
        
        (async () => {
            let comparisonId;
            const pathName = location?.pathname;
            const regexMatch = pathName?.match(/\/vijilize\/([^/]+)/);

            if (!regexMatch) {
                setIsLoading(false);
                return;
            }

            if (regexMatch && regexMatch[1]) {
                comparisonId = regexMatch[1];

                // Use forceRefresh flag to bypass cache
                const shouldForceRefresh = shouldRefresh || location.state?.timestamp;
                let patentData: any = await getPatentVijilizedDetails(
                    comparisonId,
                    !!shouldForceRefresh
                );
                
                // Clear the refresh state after data is fetched to prevent re-triggering
                if (shouldRefresh) {
                    window.history.replaceState({}, document.title);
                }
                setHeaderDataLocal((prev: any) => {
                    return {
                        ...prev,
                        vijilize: {
                            title: patentData?.name,
                            id: patentData?._id,
                        },
                    };
                });
                const patent_ids = patentData?.source.map(
                    (item: any) => item.patent_id
                );
                const compare_id = patentData?._id;
                setVijilizeParamsData({ patent_ids, compare_id });

                if (patentData?.heatmap_data) {
                    const updatedHeatmapData = await setPatentId(
                        patentData?.heatmap_data
                    );
                    const idMap: { [key: string]: string } = {};
                    updatedHeatmapData.forEach((item: any) => {
                        if (item.name && item.id) {
                            idMap[item.name] = item.id;
                        }
                    });
                    setSeriesIdMap(idMap);
                    setSeries(updatedHeatmapData);
                } else {
                    const idMap: { [key: string]: string } = {};
                    sampleHeatmapData.forEach((item: any) => {
                        if (item.name) {
                            idMap[item.name] = item.id || item.name;
                        }
                    });
                    setSeriesIdMap(idMap);
                    setSeries(sampleHeatmapData);
                }

                setAPIData(patentData);

                const countHighScores = (
                    data: any,
                    matchPercentage: number
                ) => {
                    let count = 0;

                    data?.forEach((item: any) => {
                        Object.values(item)?.forEach((claims: any) => {
                            Object.values(claims)?.forEach((claim: any) => {
                                claim?.forEach((detail: any) => {
                                    if (detail?.score > matchPercentage) {
                                        count++;
                                    }
                                });
                            });
                        });
                    });

                    return count;
                };

                const highScoreCount = countHighScores(
                    patentData?.summarized_similarity_results,
                    80
                );
                setMatchedAbove(highScoreCount);
                setIsLoading(false);
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.state, location.pathname]);

    return (
        <div className="dashboard-wrapper flex">
            <Sidebar />
            <div className="w-[calc(100%_-_72px)] ml-auto">
                <Header
                    title="Vijilize Heatmap"
                    count={0}
                    isChildEle={true}
                    type="project"
                />
                {isLoading ? <ClaimAnalysisShimmerLoader /> : null}
                {!isLoading ? (
                    <div className="pt-4 px-4 h-[calc(100vh_-_69px)] overflow-y-auto scrollbar-adjust">
                        <div className="flex justify-between items-center w-full pb-8">
                            <div className="flex items-start gap-x-2 pl-1">
                                <img
                                    className="inline-block cursor-pointer on-hover-bg-grey w-5 h-5 p-1 rounded relative top-1"
                                    src={allImgPaths.goBack}
                                    alt="return"
                                    onClick={() => navigate(-1)}
                                />
                                <div className="">
                                    <h2 className="text-[#101828] text-xl font-interSemiBold">
                                        {(APIData && APIData?.name) || "Test"}
                                    </h2>
                                    <p className="text-sm text-[#4F4D55] font-interRegular">
                                        {(APIData && APIData?.description) ||
                                            "Description of the vijilization comes here"}
                                    </p>
                                </div>
                            </div>
                            {/* <div className='flex gap-x-4 items-center'>
                                <button className={`px-3.5 py-2.5 rounded-md border border-[#E6E6E6] group`} onClick={() => { setConfigureScoreModal(true); console.log(tempData,selectedSimilarity,'RESET'); setSelectedSimilarity(tempData); }}>
                                    <span className={`text-sm font-interMedium text-[#4F4D55] group-hover:text-[#000] flex items-center gap-x-2 ${isOpen ? 'text-[#000]' : ''}`}>
                                        <img src={allImgPaths.configure} alt='' />
                                        Configure Scoring
                                    </span>
                                </button>
                            </div> */}
                        </div>

                        <div className="flex gap-x-4 mb-4 px-1.5">
                            <div className="border border-[#E6E6E6] rounded-lg bg-white flex flex-col items-center justify-center shadow-customShadow16 px-5 py-5 w-[300px]">
                                <p className="text-sm font-interRegular text-[#667085] pb-1.5">
                                    Source
                                </p>
                                <p className="text-2xl font-interBold text-[#101828]">
                                    {APIData?.source.length}
                                </p>
                            </div>
                            <div className="border border-[#E6E6E6] rounded-lg bg-white flex flex-col items-center justify-center shadow-customShadow16 px-5 py-5 w-[300px]">
                                <p className="text-sm font-interRegular text-[#424855] pb-1.5">
                                    Compare With
                                </p>
                                <p className="text-2xl font-interBold text-[#101828]">
                                    {APIData?.compare.length}
                                </p>
                            </div>
                            <div className="border border-[#E6E6E6] rounded-lg bg-white flex flex-col items-center justify-center shadow-customShadow16 px-5 py-5 w-[300px]">
                                <p className="text-sm font-interRegular text-[#667085] pb-1.5">
                                    Match above 80%
                                </p>
                                <p className="text-2xl font-interBold text-[#101828]">
                                    {matchedAbove}
                                </p>
                            </div>
                            {/* <div className="border border-[#E6E6E6] rounded-lg bg-white flex flex-col items-center justify-center shadow-customShadow16 px-5 py-5 w-[300px]">
                                <p className='text-sm font-interRegular text-[#667085] pb-1.5'>Active Patents</p>
                                <p className='text-2xl font-interBold text-[#101828]'>1</p>
                            </div> */}
                        </div>

                        <div className="flex items-center justify-between px-1.5">
                            <p className="flex items-center gap-x-2 text-base font-interMedium text-[#272E35]">
                                {showClaimLevelHeatmap
                                    ? `Claim Analysis for ${selectedSource?.name || ""
                                    }`
                                    : "Comparison Heatmap"}{" "}
                                <span className="text-sm font-interRegular text-[#7E8C9A]">
                                    {showClaimLevelHeatmap
                                        ? "(Click a box to view in depth analysis)"
                                        : "(Click on a source to view claim-level analysis)"}
                                </span>
                            </p>
                            <div className="flex gap-x-4 justify-items-end items-center">
                                {showClaimLevelHeatmap && (
                                    <button
                                        id="heatmap-back-btn"
                                        className={`mr-4`}
                                        onClick={returnToMainHeatmap}
                                    >
                                        <span
                                            className={`text-sm font-interMedium text-[#1751D0] group-hover:text-[#000] flex items-center gap-x-2`}
                                        >
                                            <img
                                                src={
                                                    allImgPaths.arrowRightContained
                                                }
                                                alt=""
                                                style={{
                                                    transform:
                                                        "rotate(180deg) ",
                                                }}
                                            />
                                            Back to Patent Level Heatmap
                                        </span>
                                    </button>
                                )}
                                {/* Heatmap Toggle Button */}
                                {/* <div className="flex items-center gap-x-2 mr-4">
                  <span className="text-sm font-interMedium text-[#4F4D55]">
                    Simple Heatmap
                  </span>
                  <button
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                      isDetailedView ? "bg-[#1751D0]" : "bg-gray-200"
                    }`}
                    onClick={() => setIsDetailedView(!isDetailedView)}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        isDetailedView ? "translate-x-6" : "translate-x-1"
                      }`}
                    />
                  </button>
                  <span className="text-sm font-interMedium text-[#4F4D55]">
                    Detailed Heatmap
                  </span>
                </div> */}

                                <button
                                    id="heatmap-skip-btn"
                                    className={``}
                                    onClick={() => {
                                        navigate(
                                            location?.pathname.split(
                                                "/heatmap"
                                            )[0]
                                        );
                                    }}
                                >
                                    <span
                                        className={`text-sm font-interMedium text-[#1751D0] group-hover:text-[#000] flex items-center gap-x-2`}
                                    >
                                        <img
                                            src={
                                                allImgPaths.arrowRightContained
                                            }
                                            alt=""
                                        />
                                        Skip Selection & Continue
                                    </span>
                                </button>
                            </div>
                        </div>

                        {APIData?.heatmap_data?.length > 0 ? (
                            <div
                                id="heatmap-chart-area"
                                className="heatmap-chart-styling"
                            >
                                {showClaimLevelHeatmap ? (
                                    <ReactApexChart
                                        options={options}
                                        series={claimLevelSeries}
                                        type="heatmap"
                                        height={550}
                                    />
                                ) : (
                                    <ReactApexChart
                                        options={options}
                                        series={series}
                                        type="heatmap"
                                        height={550}
                                    />
                                )}
                            </div>
                        ) : (
                            <>
                                <div className="flex justify-center items-center h-[calc(100vh_-_356px)]">
                                    <div className="text-center">
                                        <img
                                            className="mb-2.5 ml-auto mr-auto"
                                            src={allImgPaths.heatmapNotFound}
                                            alt=""
                                        />
                                        <p className="font-interSemiBold text-base text-[#101828] pb-2">
                                            Couldn't load the comparsion matrix
                                        </p>
                                        <p className="font-interRegular text-sm text-[#4F4D55] max-w-md mx-auto">
                                            There was some issue loading the
                                            comparison matrix. You can click
                                            next to proceed and use filters in
                                            the next screen to view your desired
                                            comparison.
                                        </p>
                                        <div className=" flex justify-center items-center gap-x-4 mt-7">
                                            {/* <button className={`px-20 py-3 rounded-md border border-[#E6E6E6] group`} >
                                                <span className={`text-sm font-interMedium text-[#4F4D55] group-hover:text-[#000] flex items-center gap-x-2 ${isOpen ? 'text-[#000]' : ''}`}>
                                                    Retry
                                                </span>
                                            </button> */}

                                            <button
                                                className={`px-20 py-3 text-sm text-white bg-[#1751D0] hover:bg-[#113B98] flex items-center gap-1.5 font-interMedium rounded-lg`}
                                                onClick={() => {
                                                    navigate(
                                                        location?.pathname.split(
                                                            "/heatmap"
                                                        )[0]
                                                    );
                                                }}
                                            >
                                                <span
                                                    className={`text-sm font-interMedium text-white group-hover:text-[#000] flex items-center gap-x-2`}
                                                >
                                                    Skip to Next Screen
                                                </span>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default ClaimAnalysisHeatmap;
